import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import { IMAGES } from "@/data/content";

const LINES = [
    <>India's finest teachers,</>,
    <>for the <em className="font-serif italic text-saffron">world's</em> curious</>,
    <>young minds.</>,
];

const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yMain = useTransform(scrollYProgress, [0, 1], [0, -70]);
    const yCard = useTransform(scrollYProgress, [0, 1], [0, 60]);

    return (
        <section ref={ref} className="relative overflow-hidden" data-testid="hero-section">
            <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-saffron-soft blur-3xl" />
            <div className="pointer-events-none absolute -left-40 bottom-0 h-[380px] w-[380px] rounded-full bg-teal-soft blur-3xl" />
            <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 pb-24 pt-16 lg:grid-cols-12 lg:px-10 lg:pb-32 lg:pt-24">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE }}
                        className="inline-flex items-center gap-2 rounded-full border border-border bg-sand px-4 py-1.5 text-xs font-medium uppercase tracking-[0.22em] text-teal"
                        data-testid="hero-eyebrow"
                    >
                        <Star className="h-3.5 w-3.5 fill-saffron text-saffron" />
                        Live online classes · From India to the world
                    </motion.p>
                    <h1 className="mt-8 font-serif text-4xl font-semibold leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="hero-headline">
                        {LINES.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className="block"
                                    initial={{ y: "110%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: EASE }}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
                        className="mt-7 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg"
                        data-testid="hero-subcopy"
                    >
                        Personalised one-on-one and small-group lessons in Hindi, English, Maths, Science and Coding —
                        taught live by experienced Indian teachers, aligned with school curricula across the US, Australia, Canada and beyond.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <Link
                            to="/free-trial"
                            data-testid="hero-book-trial-button"
                            className="group inline-flex items-center gap-2 rounded-full bg-saffron px-8 py-4 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-1 hover:bg-saffron-dark warm-shadow"
                        >
                            Book a Free Trial Lesson
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="hero-contact-link"
                            className="inline-flex items-center gap-2 rounded-full border border-teal/30 px-8 py-4 text-sm font-medium text-teal transition-all duration-300 hover:-translate-y-1 hover:border-teal hover:bg-teal hover:text-cream"
                        >
                            Talk to Us
                        </Link>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.1 }}
                        className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
                        data-testid="hero-stats"
                    >
                        {[["5", "core subjects"], ["K–12", "global curricula"], ["100%", "live & personal"]].map(([v, l]) => (
                            <div key={l}>
                                <p className="font-serif text-2xl font-semibold text-teal">{v}</p>
                                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">{l}</p>
                            </div>
                        ))}
                    </motion.div>
                </div>
                <div className="relative lg:col-span-5">
                    <motion.div
                        style={{ y: yMain }}
                        initial={{ opacity: 0, scale: 0.94 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.4, ease: EASE }}
                        className="relative z-10 overflow-hidden rounded-[2rem] border border-border lift-shadow"
                    >
                        <img
                            src={IMAGES.founderDesk}
                            alt="The founder of Hindi Ganga Online Classes teaching from her desk"
                            className="aspect-[4/3] w-full object-cover"
                            data-testid="hero-teacher-image"
                        />
                    </motion.div>
                    <motion.div
                        style={{ y: yCard }}
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
                        className="absolute -bottom-8 -left-6 z-20 rounded-2xl border border-border bg-cream p-5 lift-shadow sm:-left-10"
                        data-testid="hero-floating-card"
                    >
                        <p className="font-serif text-lg font-semibold text-ink">First lesson free</p>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Meet your teacher before<br />you commit to anything.</p>
                    </motion.div>
                    <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full border-2 border-dashed border-saffron/40" />
                </div>
            </div>
        </section>
    );
};

export default Hero;
